/*!
 * ymm-engine.js — pure, dependency-free YMM resolver + vehicle-tree navigation.
 * Environment-neutral (UMD): the browser loads it as window.YMMEngine; Node imports it for tests.
 * Faithful port of HogPlay\Fitment\FlatFitment (verdict/productIds) + the tree/selection glue.
 * NO fetch, NO DOM, NO globals beyond the single export — all state is passed in.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api; // Node / test
  if (root) root.YMMEngine = api;                                            // browser
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  function slug(s) {
    return String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  // Harley engine platform from a model NAME — mirrors build-tree.mjs classification. Used as the ffam
  // fallback when a VIN-decoded model isn't an exact tree node, so fitment still respects the platform
  // (a Softail VIN must not match Touring kits just because its model name didn't snap to the tree).
  function platform(model) {
    var m = String(model || '');
    if (/tri[\s-]*glide|freewheeler/i.test(m)) return 'Trike';
    if (/road glide|street glide|electra glide|\bultra\b|road king/i.test(m)) return 'Touring';
    return 'Softail';
  }
  function tokenKey(s) { return slug(s).split('-').sort().join('-'); }  // order-agnostic name key

  // Does a fitmap row match a (possibly partial) selection? Mirrors FlatFitment's WHERE:
  //   only provided axes constrain; '*' in a row is a wildcard; year must fall in [y0,y1].
  function rowMatches(row, sel) {
    if (sel.make != null && !(row.make === sel.make || row.make === '*')) return false;
    if (sel.family != null && !(row.family === sel.family || row.family === '*')) return false;
    if (sel.model != null && !(row.model === sel.model || row.model === '*')) return false;
    if (sel.year != null && !(sel.year >= row.y0 && sel.year <= row.y1)) return false;
    return true;
  }

  // Build a resolver selection {make,family,model,year} from a normalized vehicle.
  // family := the fitment/engine family (ffam); model constrains only when the vehicle carries one.
  function selFromVehicle(veh) {
    if (!veh) return {};
    var sel = {};
    if (veh.make != null) sel.make = veh.make;
    if (veh.ffam != null) sel.family = veh.ffam;
    if (veh.rmodel != null) sel.model = veh.rmodel;
    if (veh.year != null) sel.year = parseInt(veh.year, 10);
    return sel;
  }

  // The engine wraps a loaded dataset { tree, fitmap } and exposes sync resolver + nav methods.
  function create(data) {
    var tree = (data && data.tree) || { makes: {}, year_bands: [], family_order: [] };
    var fitmap = (data && data.fitmap) || { rows: [] };
    var rows = fitmap.rows || [];
    // SKUs that carry ANY specific (non-universal) row — the "has real fitment" set.
    var specificSkus = new Set();
    // Every SKU present in the map at all, and those with an EXPLICIT universal ('*') row.
    // FlatFitment.php treats "not in specificSkus" as universal, which silently turns MISSING DATA into
    // "fits every bike ever made". We deliberately diverge: unmapped SKUs return 'unknown' and we make
    // no claim at all. Absence of data is not a fitment guarantee.
    var knownSkus = new Set(), universalSkus = new Set();
    for (var i = 0; i < rows.length; i++) {
      knownSkus.add(rows[i].sku);
      if (rows[i].family !== '*') specificSkus.add(rows[i].sku); else universalSkus.add(rows[i].sku);
    }

    // --- resolver (FlatFitment parity) ---
    function productSkus(sel, specificOnly) {
      var out = new Set();
      for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        if (specificOnly && r.family === '*') continue;
        if (rowMatches(r, sel)) out.add(r.sku);
      }
      return Array.from(out);
    }
    // How many PRODUCTS a customer will actually see for this bike — not how many SKUs match.
    // Joe, 2026-07-16: the Fit Guide said "16 parts", the collection then showed 12. Two gaps, stacked:
    //   1. HogPlay Pro Media Box is ONE product with TWO skus (Media Pro Box S + D) → counted twice.
    //   2. TEMP-DUAL-CC / TEMP-BOOST-A / TEMP-BOOST-C are DRAFT products → counted, never shown.
    // 16 skus → 15 products → 12 published. fitmap.skuProduct (emitted by build-fitmap.mjs from the
    // catalog dump) carries {p: productId, live: bool} so we can collapse variants and drop drafts.
    // No map (older asset, or built without a dump) → fall back to the sku count, i.e. today's number.
    // A count is never a fitment claim, so degrading here is safe; being silently wrong is not.
    // Collapse a SKU LIST to a count of products a customer can actually see: multi-sku products count
    // once, drafts don't count at all. Extracted from productCount so the radio drill can reuse the
    // EXACT rule — its branches carry sku arrays rather than a vehicle selection, and a second
    // hand-rolled counter is precisely how "16 parts" vs 12 products happened the first time.
    function countProducts(skus) {
      var map = fitmap && fitmap.skuProduct;
      if (!map) return skus.length;
      var ids = {}, n = 0;
      for (var i = 0; i < skus.length; i++) {
        var m = map[skus[i]];
        if (!m) { n++; continue; }          // sku the dump never saw — count it rather than hide it
        if (m.live === false) continue;     // draft product: real, but no customer can see it
        if (!ids[m.p]) { ids[m.p] = 1; n++; }
      }
      return n;
    }
    function productCount(sel, specificOnly) { return countProducts(productSkus(sel, specificOnly)); }
    function fits(sku, sel) { return productSkus(sel, false).indexOf(sku) !== -1; }
    // 'fits' | 'universal' | 'nofit' | 'unknown'
    // 'unknown' = the SKU has NO fitment rows at all → we say nothing (see knownSkus note above).
    function verdict(sku, veh) {
      if (!knownSkus.has(sku)) return 'unknown';          // no data → make no claim
      var sel = selFromVehicle(veh);
      if (productSkus(sel, true).indexOf(sku) !== -1) return 'fits';
      if (universalSkus.has(sku)) return 'universal';     // only an EXPLICIT '*' row means universal
      return 'nofit';
    }
    // Joe 2026-07-18: a bike with ZERO specific (non-universal) fits is one of two things — split by FAMILY,
    // never by fit-count alone (a pre-2019 Street Glide has 0 supported fits but DOES have a factory radio):
    //   'screen-only' = no factory audio at all (Road King, Softail & Cruiser, no-radio Indians) → the offer is
    //                   a standalone HogPlay Media Screen + universal only.
    //   'deferred'    = faired touring WITH an OEM radio we don't support yet (98-13 / 14-19 touring) → a
    //                   SEPARATE future wire-up; leave its finder behaviour unchanged for now.
    //   'fits'        = has a supported radio (the live pages).
    function fitClass(veh) {
      var sel = selFromVehicle(veh);
      if (productSkus(sel, true).length) return 'fits';
      var DEFERRED_HD = { 'Road Glide': 1, 'Street Glide': 1, 'Electra Glide': 1, 'Ultra': 1, 'CVO': 1, 'Tri Glide': 1 };
      if (veh && veh.make === 'harley-davidson' && DEFERRED_HD[veh.vfam]) return 'deferred';
      return 'screen-only';
    }
    // Best single tag facet to link a "what fits" collection for a vehicle (JS refines precisely on page).
    function fitTag(veh) {
      if (veh && veh.ffam && veh.ffam !== '*') return 'ymm-fam:' + slug(veh.ffam);
      if (veh && veh.make) return 'ymm-make:' + slug(veh.make);
      return '';
    }
    // The SEO landing-page handle for a vehicle: fits-2024-harley-davidson-street-glide
    // Mirrors ymm-build/gen-collections.mjs exactly — that script creates them, this finds them.
    function collectionHandle(veh) {
      if (!veh || !veh.make || !veh.model || !veh.year) return '';
      return 'fits-' + veh.year + '-' + slug(veh.make) + '-' + slug(veh.model);
    }
    // Resolve a vehicle → its real landing page if one exists, else the client-filtered fallback.
    // `known` is the list of fitment collection handles Liquid found on the store (window.YMM_FIT_COLLECTIONS).
    function fitUrl(veh, known, fallbackCollection) {
      var h = collectionHandle(veh);
      if (h && known && known.indexOf(h) !== -1) return '/collections/' + h;
      return (fallbackCollection || '/collections/all') + '?ymm_fits=1';
    }

    // --- vehicle-tree navigation (YEAR-FIRST: every level can be constrained by year) ---
    function hasYear(node, year) {
      if (year == null || year === '') return true;
      var y = parseInt(year, 10);
      return (node.years || []).indexOf(y) !== -1;
    }
    // every distinct model year across the whole tree, newest first — the first drill step
    function years() {
      var s = {};
      for (var mk in tree.makes) {
        var fams = tree.makes[mk].families;
        for (var f in fams) { var mods = fams[f].models; for (var m in mods) (mods[m].years || []).forEach(function (y) { s[y] = 1; }); }
      }
      return Object.keys(s).map(Number).sort(function (a, b) { return b - a; });
    }
    function makes(year) {
      var out = [];
      for (var mk in tree.makes) {
        var fams = tree.makes[mk].families, any = false;
        for (var f in fams) { var mods = fams[f].models; for (var m in mods) { if (hasYear(mods[m], year)) { any = true; break; } } if (any) break; }
        if (any) out.push({ key: mk, name: tree.makes[mk].name || mk });
      }
      return out;
    }
    function makeName(make) { return (tree.makes[make] && tree.makes[make].name) || make; }
    function families(make, year) {
      var m = tree.makes[make];
      if (!m) return [];
      return Object.keys(m.families)
        .filter(function (f) { var mods = m.families[f].models; for (var k in mods) if (hasYear(mods[k], year)) return true; return false; })
        .map(function (f) { return { key: f, order: m.families[f].order || 99 }; })
        .sort(function (a, b) { return a.order - b.order || a.key.localeCompare(b.key); })
        .map(function (x) { return x.key; });
    }
    function models(make, family, year) {
      var f = tree.makes[make] && tree.makes[make].families[family];
      if (!f) return [];
      return Object.keys(f.models).filter(function (m) { return hasYear(f.models[m], year); }).sort();
    }
    function nodeOf(make, family, model) {
      var f = tree.makes[make] && tree.makes[make].families[family];
      return (f && f.models[model]) || null;
    }
    function yearsFor(make, family, model) {
      var n = nodeOf(make, family, model);
      return n ? (n.years || []).slice().sort(function (a, b) { return b - a; }) : []; // newest first
    }
    // Find a model node across families (e.g. from a VIN decode that only knows make+model).
    function findModel(make, model) {
      var m = tree.makes[make];
      if (!m) return null;
      var want = slug(model), wantTok = tokenKey(model), fam, name, mods;
      // pass 1: exact / slug match
      for (fam in m.families) { mods = m.families[fam].models; for (name in mods) {
        if (name === model || slug(name) === want) return { vfam: fam, model: name, node: mods[name] };
      } }
      // pass 2: order-agnostic token match ("Police Road Glide" ↔ "Road Glide Police")
      for (fam in m.families) { mods = m.families[fam].models; for (name in mods) {
        if (tokenKey(name) === wantTok) return { vfam: fam, model: name, node: mods[name] };
      } }
      return null;
    }

    // Normalize a raw pick into the canonical vehicle object the rest of the app passes around.
    // Accepts {make, family?, model?, year?}. family here is the VISUAL family (nav); resolution uses ffam.
    function buildVehicle(pick) {
      if (!pick || !pick.make) return null;
      var make = pick.make, mkName = makeName(make);
      var veh = { make: make, mk_name: mkName, vfam: pick.family || null, model: pick.model || null,
        year: pick.year != null ? parseInt(pick.year, 10) : null, ffam: null, rmodel: null };
      var found = null;
      if (pick.model) {
        found = pick.family && nodeOf(make, pick.family, pick.model)
          ? { vfam: pick.family, model: pick.model, node: nodeOf(make, pick.family, pick.model) }
          : findModel(make, pick.model);
      }
      if (found) { veh.vfam = found.vfam; veh.model = found.model; veh.ffam = found.node.ffam; veh.rmodel = found.node.rmodel; }
      else if (make === 'harley-davidson' && veh.model) { veh.ffam = platform(veh.model); veh.rmodel = null; } // VIN model not in tree → keep platform-correct
      veh.label = [veh.year, mkName, veh.model].filter(Boolean).join(' ');
      return veh;
    }

    // Inverse of collectionHandle(): "fits-2024-harley-davidson-street-glide" → the vehicle that page is
    // ABOUT. A fits-* landing page has its own bike, independent of whatever is in the visitor's garage;
    // without this the UI groups a Street Glide page by the visitor's Indian and contradicts its own H1.
    // Returns null for any handle that isn't a fitment page or doesn't resolve to a real tree year —
    // callers treat null as "no page bike" and fall back to the garage.
    function vehicleFromHandle(handle) {
      var m = /^fits-(\d{4})-(.+)$/.exec(String(handle || ''));
      if (!m) return null;
      var year = parseInt(m[1], 10), rest = m[2];
      // Both the make key and the model slug are hyphenated, so the split point is ambiguous by shape.
      // Resolve it against the real make keys, longest first (no make is a prefix of another today, but
      // don't bet the parse on that).
      var mk, best = null;
      for (mk in tree.makes) {
        if (rest.indexOf(mk + '-') === 0 && (!best || mk.length > best.length)) best = mk;
      }
      if (!best) return null;
      var found = findModel(best, rest.slice(best.length + 1));
      if (!found) return null;
      if ((found.node.years || []).indexOf(year) === -1) return null;   // page for a year we never built
      return buildVehicle({ make: best, family: found.vfam, model: found.model, year: year });
    }

    return {
      slug: slug, platform: platform, collectionHandle: collectionHandle, fitUrl: fitUrl,
      vehicleFromHandle: vehicleFromHandle,
      productSkus: productSkus, fits: fits, verdict: verdict, fitClass: fitClass, fitTag: fitTag, selFromVehicle: selFromVehicle,
      makes: makes, makeName: makeName, families: families, models: models, nodeOf: nodeOf,
      years: years, hasYear: hasYear,
      yearsFor: yearsFor, findModel: findModel, buildVehicle: buildVehicle,
      yearBands: function () { return tree.year_bands || []; },
      productCount: productCount, countProducts: countProducts,
      raw: function () { return { tree: tree, fitmap: fitmap }; },
    };
  }

  return { create: create, slug: slug, platform: platform, rowMatches: rowMatches, selFromVehicle: selFromVehicle };
});
